import React, { useState, useEffect } from 'react'
import { type AlarmData } from '../services/getHealthSummaryService'
import { APIConfiguration } from '../../../utils/apiConfiguration'
import { CircularProgress } from '@mui/material'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { useWebSocket } from '../../../contexts/WebSocketContext'
import { useDispatch } from 'react-redux'
import { type HealthSummaryState, healthSummaryWebSocketUpdateAction } from '../redux/getHealthSummarySlice'
import { type AlarmWebSocketData } from '../../../types/websocket.types'
import { type ConnectedProps, connect } from 'react-redux'
import { type RootState } from '../../../store'

const HealthSummaryCard = (props: AllProps): JSX.Element => {
  const [Efficiency, setEfficiency] = useState({
    urgentAlerts: 0,
    highAlerts: 0,
    lowAlerts: 0
  })
  const [Safety, setSafety] = useState({
    urgentAlerts: 0,
    highAlerts: 0,
    lowAlerts: 0
  })
  const [Reliability, setReliability] = useState({
    urgentAlerts: 0,
    highAlerts: 0,
    lowAlerts: 0
  })
  const [None, setNone] = useState({
    urgentAlerts: 0,
    highAlerts: 0,
    lowAlerts: 0
  })

  const [totalAlerts, setTotalAlerts] = useState({
    urgentAlerts: 0,
    highAlerts: 0,
    lowAlerts: 0
  })
  const [sumOfAlerts, setSumOfAlerts] = useState({
    Efficiency: 0,
    Safety: 0,
    Reliability: 0,
    None: 0
  })

  // WebSocket integration for real-time alarm updates
  const { messages, subscribeToDeviceAlarms, isConnected } = useWebSocket()
  const dispatch = useDispatch()
  const [subscribedAlarmDeviceId, setSubscribedAlarmDeviceId] = useState<string | null>(null)
  const [lastProcessedAlarmMessageId, setLastProcessedAlarmMessageId] = useState<string | null>(null)

  // Subscribe to WebSocket alarm updates for the current device
  // istanbul ignore next
  useEffect(() => {
    if (isConnected && props.currentDeviceId !== undefined && props.currentDeviceId !== null && props.currentDeviceId !== '') {
      // Only subscribe if we haven't already subscribed to this device
      if (subscribedAlarmDeviceId !== props.currentDeviceId) {
        subscribeToDeviceAlarms(props.currentDeviceId)
          .then((response) => {
            setSubscribedAlarmDeviceId(props.currentDeviceId ?? null)
          })
          .catch((error) => {
            console.error('Failed to subscribe to device alarms:', error)
          })
      }
    } else {
      // Reset subscription state when disconnected or no device
      if (subscribedAlarmDeviceId !== null) {
        setSubscribedAlarmDeviceId(null)
      }
    }
  }, [isConnected, props.currentDeviceId])

  // Reset subscription state when device changes
  useEffect(() => {
    if (subscribedAlarmDeviceId !== null && subscribedAlarmDeviceId !== props.currentDeviceId) {
      setSubscribedAlarmDeviceId(null)
      setLastProcessedAlarmMessageId(null)
    }
  }, [props.currentDeviceId, subscribedAlarmDeviceId])

  // Process WebSocket alarm messages
  // istanbul ignore next
  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1]

      // Create a unique message ID based on data content
      const messageId = latestMessage.correlationId ??
        `${latestMessage.type}-${JSON.stringify(latestMessage.data)}`

      // Skip if we've already processed this exact message content
      if (messageId === lastProcessedAlarmMessageId) {
        return
      }

      // Handle dashboard alarm data updates
      if (latestMessage.type === 'DASHBOARD_ALARM_DATA_UPDATE' && Boolean(latestMessage.data)) {
        const data = latestMessage.data

        // Check if this message contains alarm data for our device
        if (Array.isArray(data) && data.length > 0) {
          // Check if any alarm in the data array relates to our current device
          const relevantAlarms = data.filter((alarm: AlarmWebSocketData) =>
            Boolean(alarm.deviceIds) && Array.isArray(alarm.deviceIds) &&
            [...alarm.deviceIds, ...alarm.assetIds].includes(props.currentDeviceId ?? '')
          )

          if (relevantAlarms.length > 0) {
            // Update Redux store with new alarm data
            dispatch(healthSummaryWebSocketUpdateAction(data))
            setLastProcessedAlarmMessageId(messageId)
          }
        }
      }
    }
  }, [messages.length, props.currentDeviceId, dispatch, lastProcessedAlarmMessageId])

  useEffect(() => {
    // Reset state values to their defaults when new data is received
    setEfficiency({ urgentAlerts: 0, highAlerts: 0, lowAlerts: 0 })
    setSafety({ urgentAlerts: 0, highAlerts: 0, lowAlerts: 0 })
    setReliability({ urgentAlerts: 0, highAlerts: 0, lowAlerts: 0 })
    setNone({ urgentAlerts: 0, highAlerts: 0, lowAlerts: 0 })
    setTotalAlerts({ urgentAlerts: 0, highAlerts: 0, lowAlerts: 0 })
    setSumOfAlerts({ Efficiency: 0, Safety: 0, Reliability: 0, None: 0 })

    let totalUrgent = 0
    let totalHigh = 0
    let totalLow = 0
    if (props?.healthSummaryData?.data !== null) {
      props?.healthSummaryData?.data?.data?.forEach((alarm: AlarmData) => {
        const { alarmLowerLimitLabelName, urgentAlerts, highAlerts, lowAlerts } =
          alarm
        const alertData = { urgentAlerts, highAlerts, lowAlerts }
        switch (alarmLowerLimitLabelName) {
          case 'Efficiency':
            setEfficiency(alertData)
            setSumOfAlerts((prev) => ({
              ...prev,
              Efficiency: Number(urgentAlerts) + Number(highAlerts) + Number(lowAlerts)
            }))
            break
          case 'Safety':
            setSafety(alertData)
            setSumOfAlerts((prev) => ({
              ...prev,
              Safety: Number(urgentAlerts) + Number(highAlerts) + Number(lowAlerts)
            }))
            break
          case 'Reliability':
            setReliability(alertData)
            setSumOfAlerts((prev) => ({
              ...prev,
              Reliability: Number(urgentAlerts) + Number(highAlerts) + Number(lowAlerts)
            }))
            break
          case 'None':
            setNone(alertData)
            setSumOfAlerts((prev) => ({
              ...prev,
              None: Number(urgentAlerts) + Number(highAlerts) + Number(lowAlerts)
            }))
            break
          default:
            break
        }
        totalUrgent += Number(urgentAlerts)
        totalHigh += Number(highAlerts)
        totalLow += Number(lowAlerts)
      })

      setTotalAlerts({
        urgentAlerts: totalUrgent,
        highAlerts: totalHigh,
        lowAlerts: totalLow
      })
    }
  }, [props.healthSummaryData])

  const getAlertCountData = (): JSX.Element => {
    switch (true) {
      case props.healthSummaryData.isLoading:
        return (
          <div className="progress-bar-custom" data-testid="progress-bar" >
            <CircularProgress />
          </div>
        )

      case props.healthSummaryData.httpStatus === APIConfiguration.UNAUTHORIZED_HTTP_STATUS:
        return (
          <div className="authError" data-testid="auth-error">
            <AuthError className="paramterError" errorMessage="Unauthorized" />
          </div>
        )

      case props.healthSummaryData.httpStatus === APIConfiguration.ACCESS_FORBIDDEN_HTTP_STATUS:
        return (
          <div className="authError" data-testid="access-forbidden-error">
            <AuthError className="paramterError" errorMessage="accessForbidden" />
          </div>
        )

      case props.healthSummaryData.httpStatus === APIConfiguration.NO_DATA_FOUND_HTTP_STATUS:
        return (
          <>
            <div className='device-chart-box'>
              <div className='chart-container' style={{ display: 'flex', justifyContent: 'center' }}>
                <span className='title-alerts-first'>Safety</span>
                <div>
                  <span className='box-title'>Urgent</span>
                  <span className='high-alert'>{Safety.urgentAlerts}</span>
                </div>
                <div>
                  <span className='box-title'>High</span>
                  <span className='medium-alert'>{Safety.highAlerts}</span>
                </div>
                <div>
                  <span className='box-title'>Low</span>
                  <span className='low-alert'>{Safety.lowAlerts}</span>
                </div>
                <div>
                  <span className='box-title'>Total</span>
                  <span className='count-total'>{sumOfAlerts.Safety}</span>
                </div>
              </div>
              <div className='chart-container' style={{ display: 'flex', justifyContent: 'center', marginTop: '2%' }}>
                <span className='title-alerts'>Reliability</span>
                <div>
                  <span className='high-alert'>{Reliability.urgentAlerts}</span>
                </div>
                <div>
                  <span className='medium-alert'>{Reliability.highAlerts}</span>
                </div>
                <div>
                  <span className='low-alert'>{Reliability.lowAlerts}</span>
                </div>
                <div>
                  <span className='count-total'>{sumOfAlerts.Reliability}</span>
                </div>
              </div>
              <div className='chart-container' style={{ display: 'flex', justifyContent: 'center', marginTop: '2%' }}>
                <span className='title-alerts'>Efficiency</span>
                <div>
                  <span className='high-alert'>{Efficiency.urgentAlerts}</span>
                </div>
                <div>
                  <span className='medium-alert'>{Efficiency.highAlerts}</span>
                </div>
                <div>
                  <span className='low-alert'>{Efficiency.lowAlerts}</span>
                </div>
                <div>
                  <span className='count-total'>{sumOfAlerts.Efficiency}</span>
                </div>
              </div>
              <div className='chart-container' style={{ display: 'flex', justifyContent: 'center', marginTop: '2%' }}>
                <span className='title-alerts'>None</span>
                <div>
                  <span className='high-alert'>{None.urgentAlerts}</span>
                </div>
                <div>
                  <span className='medium-alert'>{None.highAlerts}</span>
                </div>
                <div>
                  <span className='low-alert'>{None.lowAlerts}</span>
                </div>
                <div>
                  <span className='count-total'>{sumOfAlerts.None}</span>
                </div>
              </div>
              <div className='chart-container' style={{ display: 'flex', justifyContent: 'left' }}>
                <span className='title-alerts'>Total</span>
                <div>
                  <span className='Count-alert'>{totalAlerts.urgentAlerts}</span>
                </div>
                <div style={{ marginLeft: '5px' }}>
                  <span className='Count-alert'>{totalAlerts.highAlerts}</span>
                </div>
                <div style={{ marginLeft: '5px' }}>
                  <span className='Count-alert'>{totalAlerts.lowAlerts}</span>
                </div>
              </div>
            </div>
          </>
        )

      case props.healthSummaryData.data === undefined:
        return (
          <div className="authError" data-testid="data-undefined-error">
            <AuthError className="paramterError" errorMessage="NoDataPresent" />
          </div>
        )

      case props.healthSummaryData.data !== undefined && props.healthSummaryData.httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS:
        return (
              <div className='device-chart-box' >
              <div
                className='chart-container'
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <span className='title-alerts-first'>Safety</span>
                <div>
                  <span className='box-title'>Urgent</span>
                  <span className='high-alert'>{Safety.urgentAlerts}</span>
                </div>
                <div>
                  <span className='box-title'>High</span>
                  <span className='medium-alert'>{Safety.highAlerts}</span>
                </div>
                <div>
                  <span className='box-title'>Low</span>
                  <span className='low-alert'>{Safety.lowAlerts}</span>
                </div>
                <div>
                  <span className='box-title'>Total</span>
                  <span className='count-total'>{sumOfAlerts.Safety}</span>
                </div>
              </div>
              <div
                className='chart-container'
                style={{ display: 'flex', justifyContent: 'center', marginTop: '2%' }}
              >
                <span className='title-alerts'>Reliability</span>
                <div>
                  <span className='high-alert'>{Reliability.urgentAlerts}</span>
                </div>
                <div>
                  <span className='medium-alert'>{Reliability.highAlerts}</span>
                </div>
                <div>
                  <span className='low-alert'>{Reliability.lowAlerts}</span>
                </div>
                <div>
                  <span className='count-total'>{sumOfAlerts.Reliability}</span>
                </div>
              </div>
              <div
                className='chart-container'
                style={{ display: 'flex', justifyContent: 'center', marginTop: '2%' }}
              >
                <span className='title-alerts'>Efficiency</span>
                <div>
                  <span className='high-alert'>{Efficiency.urgentAlerts}</span>
                </div>
                <div>
                  <span className='medium-alert'>{Efficiency.highAlerts}</span>
                </div>
                <div>
                  <span className='low-alert'>{Efficiency.lowAlerts}</span>
                </div>
                <div>
                  <span className='count-total'>{sumOfAlerts.Efficiency}</span>
                </div>
              </div>
              <div
                className='chart-container'
                style={{ display: 'flex', justifyContent: 'center', marginTop: '2%' }}
              >
                <span className='title-alerts'>None</span>
                <div>
                  <span className='high-alert'>{None.urgentAlerts}</span>
                </div>
                <div>
                  <span className='medium-alert'>{None.highAlerts}</span>
                </div>
                <div>
                  <span className='low-alert'>{None.lowAlerts}</span>
                </div>
                <div>
                  <span className='count-total'>{sumOfAlerts.None}</span>
                </div>
              </div>
              <div
                className='chart-container'
                style={{
                  display: 'flex',
                  justifyContent: 'left'
                }}
              >
                <span className='title-alerts'>Total</span>
                <div style={{ cursor: 'pointer' }}>
                  <span className='Count-alert'>{totalAlerts.urgentAlerts}</span>
                </div>
                <div style={{ cursor: 'pointer', marginLeft: '5px' }}>
                  <span className='Count-alert'>{totalAlerts.highAlerts}</span>
                </div>
                <div style={{ cursor: 'pointer', marginLeft: '5px' }}>
                  <span className='Count-alert'>{totalAlerts.lowAlerts}</span>
                </div>
              </div>
            </div>
        )

      default:
        return (
          <div className="authError" data-testid="healthSummaryCardError">
            <AuthError
              className="paramterError"
              errorMessage="cannotFetch"
            />
          </div>
        )
    }
  }

  return (
    getAlertCountData()
  )
}

interface StateToProps {
  healthSummaryData: HealthSummaryState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  healthSummaryData: state.HealthSummaryState
})

const connector = connect(mapStateToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

interface InputsParms {
  currentDeviceId?: string
}

type AllProps = PropsFromRedux & InputsParms
export const MemoHealthSummaryCard = React.memo(connector(HealthSummaryCard))
